import VideoPlayer from "@dana/Components/VideoPlayer";
import PrevArrowIcon from "@dana/Icons/PrevArrowIcon";
import Search from "@dana/MobiusOverrides/Icons/Search";
import Youtube from "@dana/MobiusOverrides/Icons/Youtube";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { EZIDAttributeFilter } from "@dana/Types/EZIDAttributeFilter";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img, { ImgProps } from "@insite/mobius/Img";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

interface JointAngleSelectorScreenProps {
    video?: LinkFieldValue;
    caption?: LinkFieldValue;
    backLabel?: string;
    slideTitle?: string;
    subText?: string;
    description?: string;
    image?: string;
    imageAltText?: string;
    facetLabel?: string;
    leftNote?: string;
    rightNote?: string;
    watchVideoLabel?: string;
    stepLabel?: string;
    nextLabel?: string;
    step?: number;
    readyshaftCategoryId?: string;
    currentAttributeFilters?: EZIDAttributeFilter[];
    onProceedToNextScreen: (newAttributeFilters?: EZIDAttributeFilter[]) => void;
    onProceedToPreviousScreen: (newAttributeFilters?: EZIDAttributeFilter[]) => void;
}

export interface JointAngleSelectorScreenStyles {
    wrapper?: InjectableCss;
    backButton?: ButtonPresentationProps;
    heading?: TypographyPresentationProps;
    subText?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    contentWrapper?: InjectableCss;
    twoColWrapper?: InjectableCss;
    watchButtonWrapper?: InjectableCss;
    actionWrapper?: InjectableCss;
    image?: ImgProps;
    videoModal?: ModalPresentationProps;
    watchVideoButton?: ButtonPresentationProps;
}

export const styles: JointAngleSelectorScreenStyles = {
    wrapper: {
        css: css`
                display: flex;
                flex-direction: column;
                align-items: center;
                width: 90%;
                gap: 24px;
                margin: 18px auto 0 auto;
                            ${({ theme }: { theme: BaseTheme }) =>
                                breakpointMediaQueries(
                                    theme,
                                    [
                                        css`
                                                        width: 100%;
                                                    `,
                                        css`
                                                        width: 100%;
                                                    `,
                                        css`
                                                        width: 100%;
                                                    `,
                                        null,
                                        null,
                                    ],
                                    "max",
                                )
                            }
            `,
    },
    backButton: {
        variant: "tertiary",
        css: css`
                align-self: flex-start;
                text-align: left;
            `,
        icon: {
            src: PrevArrowIcon,
            position: "left",
        },
    },
    heading: {
        variant: "h3",
        css: css`
                margin-bottom: 0;
                text-align: center;
            `,
    },
    subText: {
        variant: "h4",
        css: css`
                text-align: center;
                margin-bottom: 0;
            `,
    },
    description: {
        variant: "h5",
        transform: "none",
        color: themeColors.primary.richBlack,
        css: css`
                letter-spacing: inherit;
                text-align: center;
                margin-bottom: 0;
            `,
    },
    contentWrapper: {
        css: css`
                display: flex;
                flex-direction: column;
                align-items: center;
                width: 100%;
                border: 1px solid ${themeBackgrounds.lightGray};
                border-radius: 4px;
                padding: 16px;
                box-sizing: border-box;
    
                & > *:not(:last-child) {
                    border-bottom: 1px solid ${themeBackgrounds.lightGray};
                    padding-bottom: 16px;
                    margin-bottom: 16px;
                }
            `,
    },
    image: {
        css: css`
            width: auto;
            max-width: 100%;
            max-height: 320px;
            height: auto;
            display: block;
            object-fit: contain;
            `,
    },
    twoColWrapper: {
        css: css`
                display: flex;
                flex-direction: row;
                gap: 16px;
                width: 100%;
                & > * {
                    flex: 1 1 0;
                    min-width: 0;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                            align-items: stretch;
                            gap: 12px;
                            & > * {
                                flex: unset;
                            }
                        `,
                        css`
                            flex-direction: column;
                            align-items: stretch;
                            gap: 14px;
                            & > * {
                                flex: unset;
                            }
                        `,
                        css`
                            flex-direction: column;
                            align-items: stretch;
                            gap: 14px;
                            & > * {
                                flex: unset;
                            }
                        `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
            `,
    },
    actionWrapper: {
        css: css`
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                width: 100%;
                gap: 16px;
            `,
    },
    watchButtonWrapper: {
        css: css`
                border-bottom: none !important;
            `,
    },
    videoModal: {
        cssOverrides: {
            modalContent: css`
                video {
                    width: 100%;
                    height: auto;
                    max-height: 70vh;
                    display: block;
                }
            `,
        },
        sizeVariant: "large",
    },
    watchVideoButton: {
                variant: "primary",
                icon: {
                    position: "left",
                    src: () => <Youtube />
                },
            },
};

const JointAngleSelectorScreen: React.FC<JointAngleSelectorScreenProps> = ({
    video,
    caption,
    backLabel,
    slideTitle,
    subText,
    description,
    image,
    imageAltText,
    facetLabel,
    leftNote,
    rightNote,
    watchVideoLabel,
    stepLabel,
    nextLabel,
    step,
    readyshaftCategoryId,
    currentAttributeFilters,
    onProceedToNextScreen,
    onProceedToPreviousScreen,
}) => {
    const [showLoadingOverlay, setShowLoadingOverlay] = React.useState(true);
    const [jointAngle, setJointAngleData] = React.useState<EZIDAttributeFilter[]>([]);
    const [selectedJointAngle, setSelectedJointAngle] = useState<string[]>([]);
    const [videoModalIsOpen, setVideoModalIsOpen] = useState(false);
    const videoLink = video ? useGetLink(video) : null;
    const captionLink = caption ? useGetLink(caption) : null;
    const [jointAngleSearchText, setJointAngleSearchText] = useState("");
    const searchedJointAngleData = jointAngle.filter(angle =>
        angle.attributeValueName.toLowerCase().includes(jointAngleSearchText.toLowerCase())
    );
    const jointAngleAttributeTypeName = "Joint Angle";

    useEffect(() => {
        const searchProducts = async () => {
            try {
                const result = await fetch("/api/v1/ezid/getProductFacets", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        categoryId: readyshaftCategoryId,
                        // Exclude Joint Angle from the initial filters to get all Joint Angle data
                        attributeValueIds: currentAttributeFilters?.filter(f => f.attributeTypeName !== jointAngleAttributeTypeName)?.map(filter => filter.attributeValueId) || [],
                        groupName: jointAngleAttributeTypeName,
                    }),
                });

                if (!result.ok) {
                    throw new Error(result.statusText);
                }

                const data = await result.json();
                populateCheckboxes(data);

            } catch (error) {
                console.error("Error fetching products:", error);
            } finally {
                setShowLoadingOverlay(false);
            }
        };

        const handleExistingFilters = () => {
            if (currentAttributeFilters) {
                const jointAngleFilters = currentAttributeFilters.filter(
                    filter => filter.attributeTypeName === jointAngleAttributeTypeName
                );

                if (jointAngleFilters.length > 0) {
                    setSelectedJointAngle(jointAngleFilters.map(filter => filter.attributeValueId));
                }
            }
        }

        handleExistingFilters();
        searchProducts();
    }, []);

    const populateCheckboxes = (apiData: any) => {
        if (apiData) {
            const facets = apiData.filter(
                (facet: { attributeTypeName: string }) => facet.attributeTypeName === jointAngleAttributeTypeName);
            if (facets) {
                setJointAngleData(facets);
            }
        }
    };

    const onNextButtonClick = () => {
        // Remove all old items with attributeTypeName = "joint angle"
        const noAngleAttributeFilters =
            currentAttributeFilters?.filter(f => f.attributeTypeName !== jointAngleAttributeTypeName) || [];

        // Add selected joint angle from jointAngleData
        const selectedJointAngleFilters = searchedJointAngleData
            .filter(angle => selectedJointAngle.includes(angle.attributeValueId))
            .map(angle => ({
                attributeTypeId: angle.attributeTypeId,
                attributeTypeName: angle.attributeTypeName,
                attributeValueId: angle.attributeValueId,
                attributeValueName: angle.attributeValueName,
                count: angle.count,
            }));

        // Call onProceedToNextScreen with the new filters
        const newAttributeFilters = [...noAngleAttributeFilters, ...selectedJointAngleFilters];
        onProceedToNextScreen(newAttributeFilters);
    }

    const onBackButtonClick = () => {
        // Remove all filters with attributeTypeName = "joint angle"
        const noAngleAttributeFilters =
            currentAttributeFilters?.filter(f => f.attributeTypeName !== jointAngleAttributeTypeName) || [];
        onProceedToPreviousScreen(noAngleAttributeFilters);
    }

    const videoModalCloseHandler = () => {
        setVideoModalIsOpen(false);
    };

    return (
        <LoadingOverlay
            loading={showLoadingOverlay}
            css={css`
                width: 100%;
            `}
        >
            <StyledWrapper {...styles.wrapper} className="wrapper">
                <Button
                    {...styles.backButton}
                    onClick={onBackButtonClick}>
                    {backLabel}
                </Button>
                <Typography {...styles.heading} as="h3">
                    {slideTitle}
                </Typography>
                <Typography {...styles.subText} as="h4">
                    {subText}
                </Typography>
                <Typography {...styles.description} as="h5">
                    {description}
                </Typography>
                <StyledWrapper {...styles.contentWrapper}>
                    <StyledWrapper {...styles.twoColWrapper}>
                        {image && (
                            <Img src={image} altText={imageAltText || ''} {...styles.image} />
                        )}
                        <StyledWrapper>
                            <Typography as="b">{facetLabel} ({jointAngle.length})</Typography>
                            <TextField
                                className="angle-text-field"
                                id="angleTextField"
                                value={jointAngleSearchText}
                                onChange={e => {
                                    setJointAngleSearchText(e.target.value);
                                }}
                                iconProps={{ src: Search }}
                            />
                            <CheckboxGroup>
                                {searchedJointAngleData.map((angle) => (
                                    <Checkbox
                                        checked={selectedJointAngle.includes(angle.attributeValueId)}
                                        key={angle.attributeValueId}
                                        onChange={() => {
                                            setSelectedJointAngle((prev) => {
                                                if (prev.includes(angle.attributeValueId)) {
                                                    return prev.filter(id => id !== angle.attributeValueId);
                                                }
                                                return [...prev, angle.attributeValueId];

                                            });
                                        }}
                                    >
                                        {`${angle.attributeValueName} (${angle.count})`}
                                    </Checkbox>
                                ))}
                            </CheckboxGroup>
                        </StyledWrapper>
                    </StyledWrapper>

                    <StyledWrapper {...styles.twoColWrapper}>
                        {leftNote && (
                            <Typography className="text-body-lg">
                                {parse(leftNote, parserOptions)}
                            </Typography>
                        )}
                        {rightNote && (
                            <Typography className="text-body-lg">
                                {parse(rightNote, parserOptions)}
                            </Typography>
                        )}
                    </StyledWrapper>
                    {videoLink?.url && (
                        <StyledWrapper {...styles.watchButtonWrapper}>
                            <Button {...styles.watchVideoButton} onClick={() => setVideoModalIsOpen(true)}>
                                {watchVideoLabel}
                            </Button>
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.actionWrapper}>
                        <Typography as="b">{stepLabel} {step}</Typography>
                        <Button variant="primary" onClick={onNextButtonClick} disabled={selectedJointAngle.length === 0}>
                            {nextLabel}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <Modal
                {...styles.videoModal}
                id="angleSelectorVideoModal"
                isOpen={videoModalIsOpen}
                handleClose={videoModalCloseHandler}>
                <StyledWrapper>
                    <VideoPlayer
                        videoUrl={videoLink?.url || ''}
                        videoCaptionUrl={captionLink?.url || ''}
                    />
                </StyledWrapper>
            </Modal>
        </LoadingOverlay>
    )
};

export default JointAngleSelectorScreen;